import type { ReactElement } from 'react';
import { useState } from 'react';

import Box, { type BoxProps } from '@mui/material/Box';
import Button, { type ButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

const WrapperComponent = styled(Box)(() => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    height: '100%',

    ['&:hover .icon-wrapper, .icon-wrapper--focused']: {
        transform: 'translateY(0px)',
    },

    ['&:hover .text-wrapper, .text-wrapper--focused']: {
        opacity: 1,
    },

    ['&:focus-visible']: {
        outline: 'none',
    },
}));

const IconWrapper = styled(Box)(() => ({
    transition: 'transform 0.3s ease-in-out',
    transform: 'translateY(-10px)',
}));

const TextWrapper = styled(Box)(() => ({
    transition: 'opacity 0.3s ease-in-out',
    opacity: 0,
}));

type LabelIconButtonProps = ButtonProps & {
    renderIcon: ReactElement;
    displayText: string;
    textWrapperStyle?: BoxProps['sx'];
    iconWrapperStyle?: BoxProps['sx'];
    mainWrapperStyle?: BoxProps['sx'];
    buttonSize?: number;
};

/**
 * A button with a label and an icon that animates on hover.
 * @param {ReactElement} renderIcon - The icon to render.
 * @param {string} displayText - The text to display.
 * @param {SxProps} [textWrapperStyle] - The style of the text wrapper.
 * @param {SxProps} [iconWrapperStyle] - The style of the icon wrapper.
 * @param {SxProps} [mainWrapperStyle] - The style of the main wrapper.
 * @param {ButtonProps} [rest] - The rest of the button props.
 * @returns
 */

export const IconLabelButton = ({
    displayText,
    renderIcon,
    textWrapperStyle,
    iconWrapperStyle,
    mainWrapperStyle,
    buttonSize = 64,
    ...buttonProps
}: LabelIconButtonProps): ReactElement => {
    const [focusedVisible, setFocusedVisible] = useState(false);

    return (
        <Button
            variant="text"
            sx={{
                width: buttonSize,
                height: buttonSize,
                border: '0 !important', // We don't want a border on this button component
            }}
            onFocusVisible={(): void => {
                setFocusedVisible(true);
            }}
            onBlur={(): void => {
                setFocusedVisible(false);
            }}
            {...buttonProps}
        >
            <WrapperComponent
                sx={{
                    // eslint-disable-next-line @typescript-eslint/no-misused-spread
                    ...mainWrapperStyle,
                }}
            >
                <TextWrapper
                    className={
                        focusedVisible
                            ? 'text-wrapper--focused'
                            : 'text-wrapper'
                    }
                    sx={{
                        // eslint-disable-next-line @typescript-eslint/no-misused-spread
                        ...textWrapperStyle,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            color: 'inherit',
                        }}
                    >
                        {displayText}
                    </Typography>
                </TextWrapper>
                <IconWrapper
                    className={
                        focusedVisible
                            ? 'icon-wrapper--focused'
                            : 'icon-wrapper'
                    }
                    // eslint-disable-next-line @typescript-eslint/no-misused-spread
                    sx={{ ...iconWrapperStyle }}
                >
                    {renderIcon}
                </IconWrapper>
            </WrapperComponent>
        </Button>
    );
};
